/// <amd-module name="Wit/WorkItemForm/History/HistoryUtils" />
import { WorkItemRelation } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames, DalFields, OobFieldRefNames, WorkItemLinkConstants } from "Wit/Common/Generated/Constants";
import { IFieldLookup } from "Wit/WorkItemDataManager/Contracts";
import { isEmpty } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { IIdentity } from "VSSUI/Persona";
import { IHistoryItem } from "../Common/Services";
import { IVssPageContext } from "VSS/Platform/Context";
import { getIIdentityObject } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";

// TODO - we might be able to get this from field metadata
const calculatedFields = new Set<string | number>([
    CoreFieldRefNames.AreaId,
    CoreFieldRefNames.AuthorizedAs,
    CoreFieldRefNames.AuthorizedDate,
    CoreFieldRefNames.ChangedBy,
    CoreFieldRefNames.ChangedDate,
    CoreFieldRefNames.CommentCount,
    CoreFieldRefNames.CreatedBy,
    CoreFieldRefNames.CreatedDate,
    CoreFieldRefNames.Id,
    CoreFieldRefNames.IterationId,
    CoreFieldRefNames.NodeName,
    CoreFieldRefNames.Rev,
    CoreFieldRefNames.RevisedDate,
    CoreFieldRefNames.Watermark,
    DalFields.AreaLevel1,
    DalFields.AreaLevel2,
    DalFields.AreaLevel3,
    DalFields.AreaLevel4,
    DalFields.AreaLevel5,
    DalFields.AreaLevel6,
    DalFields.AreaLevel7,
    DalFields.IterationLevel1,
    DalFields.IterationLevel2,
    DalFields.IterationLevel3,
    DalFields.IterationLevel4,
    DalFields.IterationLevel5,
    DalFields.IterationLevel6,
    DalFields.IterationLevel7,
    DalFields.PersonID,
    OobFieldRefNames.ActivatedDate,
    OobFieldRefNames.ClosedDate,
    OobFieldRefNames.StateChangeDate,
    OobFieldRefNames.BacklogPriority,
    OobFieldRefNames.ResolvedDate
]);

/**
 * There are special generated fields that we update to track the positions of
 * work items on boards. Their ref names all look like this.
 */
const boardPositionFieldPattern = /^WEF_[A-Za-z0-9]+_/;

/**
 * @returns true if the update includes a state change, and false otherwise
 */
export function isStateUpdate(update: IHistoryItem): boolean {
    return !!getNewState(update);
}

/**
 * @returns the new state value if the state was changed, and undefined otherwise
 */
export function getNewState(update: IHistoryItem): string | undefined {
    const newValue = getNewValueForField(update, CoreFieldRefNames.State);
    return typeof newValue === "string" ? newValue : undefined;
}

/**
 * @returns the Date at which the update was made
 */
export function getUpdateDate(update: IHistoryItem): Date {
    const newChangedDate = getNewValueForField(update, CoreFieldRefNames.ChangedDate);
    if (!(newChangedDate instanceof Date)) {
        // For link-only updates, there are no fields to check. In this case, use the revised date
        return getDateInUserTimeZone(update.revisedDate);
    }
    return getDateInUserTimeZone(newChangedDate);
}

/**
 * @returns the new value for fieldRefName if it was changed in the update, and undefined if it was not changed
 */
export function getNewValueForField(update: IHistoryItem, fieldRefName: string): unknown {
    return update.fields?.[fieldRefName]?.newValue;
}

export function hasFieldChange(update: IHistoryItem, fieldRefName: string): boolean {
    const field = update.fields?.[fieldRefName];
    return !isEmpty(field?.newValue) || !isEmpty(field?.oldValue);
}

/**
 * @returns true if the field is modified automatically for all updates, and false if the field is directly modified by the user
 */
export function isCalculatedField(refName: string, fieldLookup: IFieldLookup): boolean {
    return calculatedFields.has(refName) || calculatedFields.has(fieldLookup[refName]?.id ?? "") || boardPositionFieldPattern.test(refName);
}

/**
 * @reurns true if attachments were added or deleted as part of this update
 */
export function hasAttachmentUpdates(update: IHistoryItem): boolean {
    return hasAttachmentChangesOfType(update, "added") || hasAttachmentChangesOfType(update, "removed");
}

export function hasAttachmentChangesOfType(update: IHistoryItem, changeType: keyof IHistoryItem["relations"]) {
    return update.relations?.[changeType]?.filter(isAttachmentUpdate).length > 0;
}

/**
 * @returns true if the work item relation is an attachment, and false if it is a link
 */
export function isAttachmentUpdate(relation: WorkItemRelation): boolean {
    return relation.rel === WorkItemLinkConstants.ATTACHEDLINKTYPE;
}

/**
 * @returns true if links were added, deleted, or modified as part of this update
 */
export function hasLinkUpdates(update: IHistoryItem): boolean {
    return hasLinkChangesOfType(update, "added") || hasLinkChangesOfType(update, "removed");
}

export function hasLinkChangesOfType(update: IHistoryItem, changeType: keyof IHistoryItem["relations"]) {
    return update.relations?.[changeType]?.filter(isLinkUpdate).length > 0;
}

/**
 * @returns true if the work item relation is a link, and false if it is an attachment
 */
export function isLinkUpdate(relation: WorkItemRelation): boolean {
    return !isAttachmentUpdate(relation);
}

export function getAuthorizedAsIdentity(update: IHistoryItem, pageContext: IVssPageContext): IIdentity | undefined {
    const authorizedAs = update.fields?.[CoreFieldRefNames.AuthorizedAs];
    const authorizedAsIdentity: IIdentity | undefined = getIIdentityObject(pageContext, authorizedAs?.newValue.displayName);
    return authorizedAsIdentity;
}

export function hasSameNameWithRevisedBy(update: IHistoryItem, identityToCompare: IIdentity | undefined): boolean | undefined {
    if (!identityToCompare?.displayName) {
        return false;
    }

    return localeIgnoreCaseComparer(update.revisedBy.displayName, identityToCompare.displayName) === 0;
}
